import { devlog } from '../../logic/utils.js';
import { AsText } from '../text-and-lang/as-text.js';
import { AsStepPanel } from './as-step-panel.js';
import styleText from './step-selector.css?raw';
import type { SetValueOptions } from '../inputs/types';
import { resolveSetValueSilence } from '../inputs/utils';

type StepUpdateOptions = {
  emitChange?: boolean;
  reflectAttr?: boolean;
  bypassGuards?: boolean;
};

export class AsStepSelector extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return ['show-labels', 'current-step', 'floating-panel', 'hide-panel'];
  }

  private _prevStep: number = -1;
  private _currentStep: number = -1;
  private _prevStepId: string = '';
  private _currentStepId: string = '';
  private stepPanels: AsStepPanel[] = [];
  private showLabels: boolean = true;
  private floatingPanel: boolean = false;
  private hidePanel: boolean = false;
  private observer: MutationObserver;
  private changeStepHandler?: (e: CustomEvent) => void;
  private _isReflectingCurrentStepAttr = false;
  private _pendingStep: { id: string; emit: boolean } | null = null;

  private get prevStep(): number {
    return this._prevStep;
  }

  private get currentStep(): number {
    return this._currentStep;
  }

  private set currentStep(value: number) {
    this.setStepInternal(value, {
      emitChange: false,
      reflectAttr: false,
      bypassGuards: true,
    });
  }

  private get prevStepId(): string {
    return this._prevStepId;
  }

  private get currentStepId(): string {
    return this._currentStepId;
  }

  private set currentStepId(value: string) {
    if (value === this._currentStepId) {
      return;
    }
    this._prevStepId = this._currentStepId;
    this._currentStepId = value;
  }

  private parseBooleanAttribute(value: string | null): boolean {
    if (value === null) {
      return false;
    }
    if (value === '') {
      return true;
    }
    return value.toLowerCase() === 'true';
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsStepSelector.sheet
    ];
    
    this.shadowRoot.innerHTML = /*html*/`
      <div class="step-nav">
        <div class="step-line-segments"></div>
      </div>
      <div class="step-content">
        <div class="step-panels">
          <slot></slot>
        </div>
      </div>
    `;
    
    this.observer = new MutationObserver(() => this.updateSteps());
  }

  connectedCallback() {
    this.showLabels = this.getAttribute('show-labels') !== 'false';
    this.floatingPanel = this.parseBooleanAttribute(this.getAttribute('floating-panel'));
    this.hidePanel = this.parseBooleanAttribute(this.getAttribute('hide-panel'));
    this.setInitialCurrentStep();
    
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'hide']
    });
    
    setTimeout(() => {
      this.updateSteps();
      this.updatePanelLayout();
    }, 0);

    // Add window event listener for global step changes
    this.changeStepHandler = (e: CustomEvent) => {
      const { stepId, stepIndex } = e.detail || {};

      let targetIndex = -1;
      if (stepIndex !== undefined) {
        targetIndex = stepIndex;
      } else if (stepId) {
        targetIndex = this.stepPanels.findIndex(panel => panel.id === stepId);
      }

      // Only navigate if it's a different step
      if (targetIndex !== -1 && targetIndex !== this.currentStep) {
        this.goToStep(targetIndex);
      }
    };
    window.addEventListener('as-change-step', this.changeStepHandler);
  }

  disconnectedCallback() {
    this.observer.disconnect();
    if (this.changeStepHandler) {
      window.removeEventListener('as-change-step', this.changeStepHandler);
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (name === 'show-labels') {
      this.showLabels = newValue !== 'false';
      this.updateSteps();
    } else if (name === 'current-step') {
      if (this._isReflectingCurrentStepAttr) {
        return;
      }
      this.setCurrentStepFromAttribute(newValue, { emitChange: true });
    } else if (name === 'floating-panel') {
      this.floatingPanel = this.parseBooleanAttribute(newValue);
      this.updatePanelLayout();
    } else if (name === 'hide-panel') {
      this.hidePanel = this.parseBooleanAttribute(newValue);
      this.updatePanelVisibility();
      this.updatePanelLayout();
    }
  }

  private setInitialCurrentStep() {
    const currentStepAttr = this.getAttribute('current-step') ?? '0';
    this.setCurrentStepFromAttribute(currentStepAttr, { emitChange: false });
  }

  private setCurrentStepFromAttribute(value: string | null, options: StepUpdateOptions = {}) {
    if (value === null) {
      return;
    }

    const stepOptions: StepUpdateOptions = {
      emitChange: options.emitChange ?? true,
      reflectAttr: false,
      bypassGuards: true,
    };

    // Check if it's a number or string ID
    const numericValue = parseInt(value, 10);
    const isNumeric = !isNaN(numericValue) && numericValue.toString() === value.trim();

    if (isNumeric) {
      // Numeric index path - simple and direct
      this.setStepInternal(numericValue, stepOptions);
    } else {
      // String ID path - need to find the panel by ID
      const targetIndex = this.stepPanels.findIndex(panel => panel.id === value);

      if (targetIndex !== -1) {
        this.setStepInternal(targetIndex, stepOptions);
      } else {
        // Step not found yet, store as pending for updateSteps() to handle
        this._pendingStep = { id: value, emit: stepOptions.emitChange ?? true };
      }
    }
  }

  private updateSteps() {
    // Get all step panels, including hidden ones for internal tracking
    this.stepPanels = Array.from(this.querySelectorAll('as-step-panel'));
    this.renderStepNav();

    if (this._pendingStep) {
      const { id, emit } = this._pendingStep;
      const targetIndex = this.stepPanels.findIndex(panel => panel.id === id);
      if (targetIndex !== -1) {
        this.setStepInternal(targetIndex, {
          emitChange: emit,
          reflectAttr: false,
          bypassGuards: true,
        });
        this._pendingStep = null;
      }
    }

    this.updateStepStates();
    this.updatePanelVisibility();
    this.updatePanelLayout();
  }

  private renderStepNav() {
    const stepNav = this.shadowRoot?.querySelector('.step-nav');
    const lineSegments = this.shadowRoot?.querySelector('.step-line-segments');
    if (!stepNav || !lineSegments) return;

    // Clear existing items (except line segments container)
    Array.from(stepNav.children).forEach(child => {
      if (!child.classList.contains('step-line-segments')) {
        child.remove();
      }
    });
    lineSegments.innerHTML = '';

    // Filter out hidden step panels
    const visibleStepPanels = this.stepPanels.filter(panel => !panel.hasAttribute('hide'));

    // If no visible step panels, create default step 0
    if (visibleStepPanels.length === 0) {
      const stepItem = document.createElement('div');
      stepItem.className = 'step-item';

      const stepButton = document.createElement('div');
      stepButton.className = 'step-button';
      stepButton.textContent = '0';
      stepItem.appendChild(stepButton);

      stepNav.appendChild(stepItem);
      return;
    }

    // Create line segments (visible steps - 1)
    for (let i = 0; i < visibleStepPanels.length - 1; i++) {
      const segment = document.createElement('div');
      segment.className = 'step-line-segment';
      lineSegments.appendChild(segment);
    }

    visibleStepPanels.forEach((panel, visibleIndex) => {
      const actualIndex = this.stepPanels.indexOf(panel);
      const stepItem = document.createElement('div');
      stepItem.className = 'step-item';

      const stepButton = document.createElement('div');
      stepButton.className = 'step-button';
      stepButton.textContent = (visibleIndex + 1).toString();

      // Check if this step should be disabled (either directly disabled or blocked by earlier disabled step)
      const isBlocked = this.isStepBlocked(actualIndex);

      if (isBlocked) {
        stepButton.classList.add('disabled');
      } else {
        // Prevent duplicate event listeners
        stepButton.onclick = () => this.goToStep(actualIndex);
      }

      stepItem.appendChild(stepButton);

      if (this.showLabels) {
        const label = document.createElement('as-text');
        label.className = 'step-label';
        const dictKey = panel.getAttribute('label-dict-key');
        const defaultText = panel.getAttribute('label-default-text');

        if (dictKey) {
          label.setAttribute('dict-key', dictKey);
        }
        if (defaultText) {
          label.textContent = defaultText;
        }

        // Add disabled class and prevent interactions if blocked
        if (isBlocked) {
          label.classList.add('disabled');
        } else {
          // Prevent duplicate event listeners by using onclick properties
          label.onclick = () => this.goToStep(actualIndex);
        }

        stepItem.appendChild(label);
      }

      stepNav.appendChild(stepItem);
    });
  }

  private updateStepStates() {
    const buttons = this.shadowRoot?.querySelectorAll('.step-button');
    const segments = this.shadowRoot?.querySelectorAll('.step-line-segment');
    const labels = this.shadowRoot?.querySelectorAll('.step-label');
    if (!buttons) return;

    // Get visible step panels (non-hidden) to match with buttons/labels
    const visibleStepPanels = this.stepPanels.filter(panel => !panel.hasAttribute('hide'));

    buttons.forEach((button, visibleIndex) => {
      const panel = visibleStepPanels[visibleIndex];
      const actualIndex = this.stepPanels.indexOf(panel);

      button.className = 'step-button';

      if (this.isStepBlocked(actualIndex)) {
        button.classList.add('disabled');
      } else if (actualIndex < this.currentStep) {
        button.classList.add('completed');
      } else if (actualIndex === this.currentStep) {
        button.classList.add('current');
      }
    });

    // Update labels - labels for completed steps should be green, disabled/blocked steps should be grayed
    labels.forEach((label, visibleIndex) => {
      const panel = visibleStepPanels[visibleIndex];
      const actualIndex = this.stepPanels.indexOf(panel);

      label.className = 'step-label';

      if (this.isStepBlocked(actualIndex)) {
        label.classList.add('disabled');
      } else if (actualIndex < this.currentStep) {
        label.classList.add('completed');
      }
    });

    // Update line segments - segments leading up to current step should be green
    segments.forEach((segment, visibleIndex) => {
      const panel = visibleStepPanels[visibleIndex];
      const actualIndex = this.stepPanels.indexOf(panel);

      segment.className = 'step-line-segment';
      if (actualIndex < this.currentStep) {
        segment.classList.add('completed');
      }
    });
  }

  private updatePanelVisibility() {
    let hasCurrent = false;
    this.stepPanels.forEach((panel, index) => {
      const isCurrent = index === this.currentStep;
      if (!this.hidePanel && isCurrent) {
        panel.setAttribute('show', '');
      } else {
        panel.removeAttribute('show');
      }

      if (isCurrent) {
        hasCurrent = true;
        const panelId = panel.id || '';
        if (this.currentStepId !== panelId) {
          this.currentStepId = panelId;
        }
      }
    });

    if (!hasCurrent && this.currentStepId !== '') {
      this.currentStepId = '';
    }
  }

  private updatePanelLayout() {
    const stepPanels = this.shadowRoot?.querySelector('.step-panels');
    if (!stepPanels) return;

    stepPanels.classList.toggle('inline', !this.floatingPanel);
    stepPanels.classList.toggle('hidden', this.hidePanel);
  }

  private isStepBlocked(stepIndex: number): boolean {
    let isBlocked = false
    if(this.stepPanels?.[stepIndex]===undefined)return true
    // A step is blocked if it's directly disabled
    if (this.stepPanels[stepIndex].hasAttribute('disabled')) {
      isBlocked = true;
    }

    // A step is blocked if going forward from current position and there's a disabled step in the way
    else if (stepIndex > this.currentStep) {
      for (let i = this.currentStep + 1; i <= stepIndex; i++) {
        if (this.stepPanels[i].hasAttribute('disabled')) {
          isBlocked =  true;
        }
      }
    }

    else isBlocked =  false;
    return isBlocked
  }

  private clampStepIndex(stepIndex: number): number {
    if (this.stepPanels.length === 0) {
      return Math.max(stepIndex, 0);
    }
    if (stepIndex < 0) {
      return 0;
    }
    if (stepIndex >= this.stepPanels.length) {
      return this.stepPanels.length - 1;
    }
    return stepIndex;
  }

  private reflectCurrentStepAttr(stepIndex: number): void {
    if (this._isReflectingCurrentStepAttr) {
      return;
    }
    this._isReflectingCurrentStepAttr = true;
    try {
      this.setAttribute('current-step', stepIndex.toString());
    } finally {
      this._isReflectingCurrentStepAttr = false;
    }
  }

  private emitStepChange(prevIndex: number, prevId: string, newIndex: number, newId: string): void {
    const detail = {
      stepId: newId,
      index: newIndex,
      prevStepId: prevId,
      prevStepIdx: prevIndex,
      prevStepIndex: prevIndex,
      newStepId: newId,
      newStepIdx: newIndex,
      currentStepId: newId,
      currentStepIndex: newIndex,
      phase: 'change' as const,
    };

    this.dispatchEvent(new CustomEvent('as-step-changed', {
      detail,
      bubbles: true,
      composed: true,
    }));

    this.dispatchEvent(new CustomEvent('as-step-change', {
      detail,
      bubbles: true,
      composed: true,
    }));
  }

  private setStepInternal(stepIndex: number, options: StepUpdateOptions = {}): boolean {
    const { emitChange = true, reflectAttr = true, bypassGuards = false } = options;

    if (!bypassGuards && this.isStepBlocked(stepIndex)) {
      return false;
    }

    const clampedIndex = this.clampStepIndex(stepIndex);
    const previousIndex = this._currentStep;
    const previousId = this._currentStepId;

    if (previousIndex === clampedIndex) {
      if (reflectAttr) {
        this.reflectCurrentStepAttr(clampedIndex);
      }
      return false;
    }

    this._prevStep = previousIndex;
    this._currentStep = clampedIndex;

    const newPanel = this.stepPanels[clampedIndex];
    const newId = newPanel?.id ?? '';
    this.currentStepId = newId;

    if (reflectAttr) {
      this.reflectCurrentStepAttr(clampedIndex);
    }

    this.updateStepStates();
    this.updatePanelVisibility();

    if (emitChange) {
      this.emitStepChange(previousIndex, previousId, clampedIndex, newId);
    }

    return true;
  }

  public setStep(stepIndex: number, opt?: SetValueOptions): boolean {
    const { silenceChange } = resolveSetValueSilence(opt);

    return this.setStepInternal(stepIndex, {
      emitChange: !silenceChange,
      reflectAttr: true,
      bypassGuards: false,
    });
  }

  private goToStep(stepIndex: number) {
    if (stepIndex < 0 || stepIndex >= this.stepPanels.length) {
      return;
    }

    if (stepIndex === this.currentStep) {
      this.hidePanel = !this.hidePanel;
      this.updatePanelVisibility();
      return;
    } else if (this.hidePanel) {
      this.hidePanel = false;
      this.updatePanelVisibility();
    }

    const changed = this.setStep(stepIndex);
    if (!changed) {
      devlog(`[STEP-SELECTOR] Unable to navigate to step ${stepIndex}`);
    }
  }

  public nextStep() {
    if (this.currentStep < this.stepPanels.length - 1) {
      this.goToStep(this.currentStep + 1);
    }
  }

  public previousStep() {
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }

  public getCurrentStep(): number {
    return this.currentStep;
  }

  public getTotalSteps(): number {
    return this.stepPanels.length;
  }

  get value(): number {
    return this._currentStep;
  }

  set value(stepIndex: number) {
    this.setStep(stepIndex);
  }
}

if (!customElements.get('as-step-selector')) {
  customElements.define('as-step-selector', AsStepSelector);
} else {
  console.warn("as-step-selector already defined");
} 
